// @flow

import React, { Fragment, useState } from 'react';
import noop from 'lodash';
import cx from 'classnames';
import styles from './styles.module.scss';
import Button from '../Button';
import FreeModal from 'components/FreeModal/FreeModal';

export type Value = string | number;

export type ToggleOption = {
  label: string,
  textAdd?: string,
  value: Value,
};

type Props = {
  onToggle?: (option: ToggleOption) => void,
  options: ToggleOption[],
  selected?: Value,
  size?: 'sm' | 'md' | 'xs',
  fill?: boolean,
  className?: string,
  use?: string,
  disabled?: boolean,
  controlToggler?: boolean,
  withBackground?: boolean,
  mttIsDisabled?: boolean,
  stack20isDisabled?: boolean,
  huIsDisabled?: boolean,
  is2rowsToggler?: boolean,
  limitIsHU?: boolean,
  selectedTSisAnte?: boolean,
  selectedTsIsMedium?: boolean,
  selectedGameTableIsHU?: boolean,
  isFreeUser?: boolean,
  isMTTAntePotSQZ?: boolean,
  isMTTAntePot10?: boolean,
  difficultyIsDisabled?: boolean,
  isPercentsResolver?: boolean,
  forResults?: boolean,
  forPostflop?: boolean,
  postflopOptions?: any,
  boardSuitIsMono?: boolean,
  boardTypeIsPaired?: boolean,
};

const Toggler = ({
  onToggle = noop,
  options,
  selected,
  size = 'md',
  fill = true,
  className,
  use = 'default',
  disabled = false,
  mttIsDisabled,
  controlToggler = false,
  withBackground = true,
  stack20isDisabled,
  huIsDisabled,
  limitIsHU,
  is2rowsToggler = false,
  selectedTSisAnte,
  selectedTsIsMedium,
  selectedGameTableIsHU,
  isFreeUser,
  isMTTAntePot10,
  isMTTAntePotSQZ,
  difficultyIsDisabled,
  isPercentsResolver = false,
  forResults = false,
  forPostflop = false,
  postflopOptions,
  boardSuitIsMono = false,
  boardTypeIsPaired = false,
}: Props) => {
  const postflopCheck = (value: string) => {
    if (!postflopOptions) {
      return false;
    }
    const res = postflopOptions.includes(value);
    return !res;
  };

  const checkDisable = (value: string) => {
    if (mttIsDisabled && (value === 'mttpot' || value === 'mtt2_25x')) return true;
    if (stack20isDisabled && (value === '20bb')) return true;
    if (huIsDisabled && (value === 'hu')) return true;
    if (limitIsHU && (value === 'SQZ' || value === 'VS SQZ' || value === '4BET')) return true;
    if (selectedTSisAnte && (['20', '50', '75', '200', '20bb', '50bb', '75bb', '200bb'].includes(value))) return true;
    if (isMTTAntePot10 && (value === 'SQZ' || value === 'VS SQZ')) return true;
    if (isMTTAntePotSQZ && (value === '10bb' || value === '10')) return true;
    if (difficultyIsDisabled && (value === 'easy' || value === 'hard')) return true;
    if (selectedTsIsMedium && (value === '200' || value === '200bb')) return true;
    if (selectedGameTableIsHU && (value === '200' || value === '200bb')) return true;
    if (boardSuitIsMono && value === 'paired') return true;
    if (boardTypeIsPaired && value === 'mono') return true;
    if (disabled && isPercentsResolver) return true;
    if (isFreeUser) return true;
    if (forPostflop) return postflopCheck(value);
    return false;
  };

  const [freeModalVisible, setFreeModalVisible] = useState<boolean>(false);

  return (
    <div className={cx(styles.relative, withBackground ? styles.withBackground : '', className, controlToggler ? styles.controlToggler : '', is2rowsToggler ? styles.rowsWrapper : '')}>
      <div className={
        cx(styles.toggler, styles[size],
          { [styles.fill]: fill, [styles.rowsToggler]: is2rowsToggler })
      }
      >
        {is2rowsToggler && <div className={styles.divider} />}
        {options.map((option) => (
          <Fragment key={JSON.stringify(option.value)}>
            <Button
              disabled={checkDisable(option.value) || disabled}
              small={size === 'sm'}
              onClick={() => (checkDisable(option.value) ? null : onToggle(option))}
              className={cx(
                styles.toggle,
                is2rowsToggler ? styles.rowButton : '',
                styles[size],
                {
                  [styles.selected]:
                  selected === option
                  || selected.value === option.value
                  || selected.label === option.label
                  || selected === option.value
                  || selected === option.label,
                },
                styles[use],
              )}
              setFreeModalVisible={setFreeModalVisible}
              isFreeUser={isFreeUser}
            >
              {option.textAdd ? (
                <span className={styles.toggleLabelWithSubtext}>
                  <span>{option.label}</span>
                  <span className={styles.toggleSubtext}>{option.textAdd}</span>
                </span>
              ) : option.label}
            </Button>
            <div className={styles.line} />
          </Fragment>
        ))}
      </div>
      {controlToggler && <FreeModal visible={freeModalVisible} forControl={forResults} />}
    </div>
  );
};

export default Toggler;
